import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Modal,
  Progress,
  Row,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useUiState } from "../state";
import { textMap } from "../i18n";
import {
  fetchBillingHistory,
  fetchBillingSummary,
  fetchReviewHistory,
  type BillingHistoryItem,
  type BillingSummary,
  type ReviewHistoryItem,
} from "../services/compliance";

const { Title, Paragraph, Text } = Typography;

export function BillingPage() {
  const { lang } = useUiState();
  const navigate = useNavigate();
  const t = textMap[lang];
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [historyRows, setHistoryRows] = useState<BillingHistoryItem[]>([]);
  const [reviewRows, setReviewRows] = useState<ReviewHistoryItem[]>([]);
  const [reviewDetailOpen, setReviewDetailOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<ReviewHistoryItem | null>(null);

  useEffect(() => {
    let mounted = true;
    Promise.all([fetchBillingSummary(), fetchBillingHistory(), fetchReviewHistory()]).then(
      ([billing, history, reviews]) => {
      if (!mounted) {
        return;
      }
      setSummary(billing);
      setHistoryRows(history);
      setReviewRows(reviews);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const currentPlanText = useMemo(() => {
    if (summary?.plan?.name) {
      return summary.plan.name;
    }
    return lang === "zh" ? "Free（未开通）" : "Free (No active plan)";
  }, [lang, summary]);

  const renewText = useMemo(() => {
    if (summary?.renew_at) {
      const date = new Date(summary.renew_at).toLocaleDateString(
        lang === "zh" ? "zh-CN" : "en-US",
      );
      return lang === "zh" ? `下次扣费：${date}` : `Next renewal: ${date}`;
    }
    return lang === "zh" ? "下次扣费：-" : "Next renewal: -";
  }, [lang, summary]);

  const usageText = useMemo(() => {
    const used = summary?.month_used_scans ?? 0;
    if (summary?.is_unlimited) {
      return lang === "zh" ? `${used} / 不限次` : `${used} / Unlimited`;
    }
    if (typeof summary?.month_scan_quota === "number") {
      return `${used} / ${summary.month_scan_quota}`;
    }
    return `${used}`;
  }, [lang, summary]);

  const usagePercent = useMemo(() => {
    if (summary?.is_unlimited) {
      return 0;
    }
    if (typeof summary?.month_scan_quota === "number" && summary.month_scan_quota > 0) {
      return Math.min(
        100,
        Math.round(((summary.month_used_scans ?? 0) / summary.month_scan_quota) * 100),
      );
    }
    return 0;
  }, [summary?.is_unlimited, summary?.month_scan_quota, summary?.month_used_scans]);

  const columns = [
    { title: t.billingHistoryColumns[0], dataIndex: "date", key: "date" },
    { title: t.billingHistoryColumns[1], dataIndex: "item", key: "item" },
    { title: t.billingHistoryColumns[2], dataIndex: "amount", key: "amount" },
    { title: t.billingHistoryColumns[3], dataIndex: "status", key: "status" },
  ];

  const reviewColumns = [
    {
      title: lang === "zh" ? "日期" : "Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: lang === "zh" ? "文案片段" : "Copy snippet",
      dataIndex: "input",
      key: "input",
    },
    {
      title: lang === "zh" ? "合规分" : "Score",
      dataIndex: "score",
      key: "score",
    },
    {
      title: lang === "zh" ? "详情" : "Details",
      dataIndex: "actions",
      key: "actions",
    },
  ];

  const dataSource = historyRows.map((row) => ({
    key: row.id,
    date: new Date(row.created_at).toLocaleDateString(lang === "zh" ? "zh-CN" : "en-US"),
    item: row.order_no || (lang === "zh" ? "订阅订单" : "Subscription order"),
    amount: `$${(row.amount_usd_cents / 100).toFixed(2)}`,
    status: (
      <Tag color={row.status === "paid" ? "green" : "default"}>
        {row.status === "paid"
          ? lang === "zh"
            ? "已支付"
            : "Paid"
          : lang === "zh"
            ? "待处理"
            : "Pending"}
      </Tag>
    ),
  }));

  const reviewDataSource = reviewRows.map((row) => ({
    key: row.id,
    date: new Date(row.created_at).toLocaleDateString(lang === "zh" ? "zh-CN" : "en-US"),
    input: row.input_text.slice(0, 40),
    score: (
      <Tag color={row.score >= 85 ? "green" : row.score >= 70 ? "gold" : "red"}>
        {row.score}/100
      </Tag>
    ),
    actions: (
      <Button
        size="small"
        onClick={() => {
          setSelectedReview(row);
          setReviewDetailOpen(true);
        }}
      >
        {lang === "zh" ? "查看详情" : "View details"}
      </Button>
    ),
  }));

  return (
    <>
      <div className="billing-sentinel">
        <div className="billing-sentinel-hero">
          <Title level={2} className="billing-sentinel-title">
            {lang === "zh" ? "账单与点数" : "Billing & Credits"}
          </Title>
          <Paragraph className="billing-sentinel-subtitle">
            {lang === "zh"
              ? "管理你的订阅、点数消耗与账单记录。"
              : "Manage your subscription, credit usage, and billing records."}
          </Paragraph>
        </div>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card className="billing-glass-card billing-plan-card" bordered={false}>
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <div className="billing-plan-head">
                  <div>
                    <Text className="billing-kicker">
                      {lang === "zh" ? "当前方案" : "Current plan"}
                    </Text>
                    <Title level={2} className="billing-plan-title">
                      {currentPlanText}
                    </Title>
                  </div>
                  <Tag color={summary?.plan ? "green" : "default"}>
                    {summary?.plan
                      ? lang === "zh"
                        ? "生效中"
                        : "Active"
                      : lang === "zh"
                        ? "未开通"
                        : "Inactive"}
                  </Tag>
                </div>
                <div>
                  <Text className="billing-kicker">
                    {lang === "zh" ? "剩余点数" : "Credit balance"}
                  </Text>
                  <Title level={1} className="billing-credit-balance">
                    {(summary?.credit_balance ?? 0).toLocaleString()}
                  </Title>
                </div>
                <div className="billing-plan-foot">
                  <div>
                    <Text type="secondary">
                      {lang === "zh" ? "续费日期" : "Renewal date"}
                    </Text>
                    <br />
                    <Text strong>{renewText.replace(/^.*[:：]\s*/, "")}</Text>
                  </div>
                  <Button
                    type="primary"
                    className="billing-upgrade-btn"
                    onClick={() => navigate("/app/upgrade")}
                  >
                    {lang === "zh" ? "升级" : "Upgrade"}
                  </Button>
                </div>
              </Space>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card className="billing-glass-card billing-usage-card" bordered={false}>
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Text className="billing-kicker">{lang === "zh" ? "使用情况" : "Usage"}</Text>
                <Text strong>
                  {lang === "zh" ? "本月消耗" : "Monthly consumption"}
                </Text>
                <div className="billing-usage-headline">
                  <Title level={2} style={{ margin: 0 }}>
                    {summary?.is_unlimited ? "-" : `${usagePercent}%`}
                  </Title>
                  <Text type="secondary">{usageText}</Text>
                </div>
                <Progress
                  percent={summary?.is_unlimited ? 100 : usagePercent}
                  showInfo={false}
                  strokeColor={{
                    "0%": "#003d9b",
                    "100%": "#5b8dff",
                  }}
                />
                <Row gutter={[12, 12]}>
                  <Col span={12}>
                    <div className="billing-mini-metric">
                      <Text type="secondary" className="billing-mini-label">
                        {lang === "zh" ? "AI 审查次数" : "AI reviews"}
                      </Text>
                      <Text strong>{(summary?.month_used_scans ?? 0).toLocaleString()}</Text>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="billing-mini-metric">
                      <Text type="secondary" className="billing-mini-label">
                        {lang === "zh" ? "账单记录数" : "Billing records"}
                      </Text>
                      <Text strong>{historyRows.length.toLocaleString()}</Text>
                    </div>
                  </Col>
                </Row>
              </Space>
            </Card>
          </Col>
        </Row>

        <Card className="billing-payment-card" bordered={false}>
          <div className="billing-payment-wrap">
            <div className="billing-payment-left">
              <div className="billing-card-chip">VISA</div>
              <div>
                <Text strong>
                  {lang === "zh" ? "支付方式" : "Payment method"}
                </Text>
                <br />
                <Text type="secondary">
                  {lang === "zh"
                    ? "默认支付方式（接口未返回卡信息）"
                    : "Default payment method (card details unavailable in API)"}
                </Text>
              </div>
            </div>
            <Button disabled>{lang === "zh" ? "更新" : "Update"}</Button>
          </div>
        </Card>

        <Card className="billing-history-card" bordered={false}>
          <div className="billing-table-head">
            <Title level={5} style={{ margin: 0 }}>
              {t.billingHistoryTitle}
            </Title>
          </div>
          <Table
            columns={columns}
            dataSource={dataSource}
            size="middle"
            pagination={false}
            locale={{
              emptyText:
                lang === "zh" ? "暂无账单记录" : "No billing records",
            }}
          />
        </Card>

        <Card
          className="billing-history-card"
          bordered={false}
          title={lang === "zh" ? "审查记录（含评分）" : "Review records (with score)"}
        >
          <Table
            columns={reviewColumns}
            dataSource={reviewDataSource}
            size="small"
            pagination={{ pageSize: 8 }}
            locale={{
              emptyText:
                lang === "zh" ? "暂无审查记录" : "No review records",
            }}
          />
        </Card>
      </div>
      <Modal
        open={reviewDetailOpen}
        onCancel={() => setReviewDetailOpen(false)}
        footer={null}
        title={lang === "zh" ? "审查详情与改进方案" : "Review details & improvement plan"}
        width={760}
      >
        <Space direction="vertical" size={10} style={{ width: "100%" }}>
          <Text strong>
            {lang === "zh" ? "合规分" : "Compliance score"}: {selectedReview?.score ?? 0}/100
          </Text>
          <Text type="secondary">{selectedReview?.summary ?? ""}</Text>
          <Card size="small" title={lang === "zh" ? "原文片段" : "Original copy"}>
            <Text>{selectedReview?.input_text ?? "-"}</Text>
          </Card>
          {(selectedReview?.flagged_items ?? []).map((item, idx) => (
            <Card
              key={`${selectedReview?.id ?? "review"}-${idx}`}
              size="small"
              title={`${lang === "zh" ? "问题" : "Issue"} ${idx + 1}`}
            >
              <Space direction="vertical" size={6} style={{ width: "100%" }}>
                <Text strong>
                  {lang === "zh" ? "片段：" : "Phrase: "} {item.phrase}
                </Text>
                <Text type="secondary">
                  {lang === "zh" ? "原因：" : "Reason: "} {item.reason}
                </Text>
                <Text>
                  {lang === "zh" ? "改进方案：" : "Improvement: "} {item.suggestion}
                </Text>
              </Space>
            </Card>
          ))}
        </Space>
      </Modal>
    </>
  );
}
